import { gql, useMutation, useQuery } from '@apollo/client'
import { useEffect, useMemo, useState } from 'react'

import {
  ArtistMembershipPurchaseConfirmationQuery,
  ArtistMembershipPurchaseConfirmationQueryVariables,
  ArtistMembershipSubscriptionDetailFragmentDoc,
  PaymentDetailFragmentDoc,
  PaymentMethod,
  StartPendinggSubscriptionMutation,
  StartPendinggSubscriptionMutationVariables,
  StartSubscription,
  StartSubscriptionMutation,
  StartSubscriptionMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config, constants } from '@/config'
import { useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { getActiveMembershipSubscriptionProductPlan } from '@/core/membership'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'
import {
  getPrefrenceObject,
  removePrefernce,
  setRegisterNewMembership,
} from '@/core/native/storage'

import { ArtistMembershipPurchaseInput } from '../ArtistMembershipPurchaseProceed/template/ArtistMembershipPurchaseForm'
import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, generatePath, routerPages } =
    useRouter<{ artistId: string; productId: string }>()
  const { showError } = useMessage()
  const { showModal } = useSettingUserInfoModal()

  const [initialPlan, setInitialPlan] =
    useState<ArtistMembershipPurchaseInput>()

  const artistId =
    (config.applicationType === 'standalone'
      ? config.artistId
      : router.query.artistId) ?? '#'

  const { data, loading } = useQuery<
    ArtistMembershipPurchaseConfirmationQuery,
    ArtistMembershipPurchaseConfirmationQueryVariables
  >(QUERY, { variables: { id: artistId } })

  const [startSubscription, { loading: startingSubscription }] = useMutation<
    StartSubscriptionMutation,
    StartSubscriptionMutationVariables
  >(StartSubscription, {
    async onCompleted({ startSubscription }) {
      await removePrefernce('artistMembershipPurchaseInput')
      await setRegisterNewMembership()
      if (startSubscription?.isPersonalInformationRequired) {
        router.replace(generatePath(routerPages.ArtistTop, { artistId }))
        return showModal({
          showSnackbarWhenClose: true,
          isRegisterNewMembership: true,
        })
      }
      return router.replace(
        generatePath(routerPages.ArtistMembershipCompletion, {
          artistId,
          productId: router.query.productId,
        })
      )
    },
  })

  const [startPendingSubscription, { loading: startingPendingSubscription }] =
    useMutation<
      StartPendinggSubscriptionMutation,
      StartPendinggSubscriptionMutationVariables
    >(START_PENDING_SUBSCRIPTION, {
      async onCompleted({ startPendinggSubscription }) {
        await removePrefernce('artistMembershipPurchaseInput')
        await setRegisterNewMembership()
        let pendingPurchaseId = startPendinggSubscription.id
        if ('artist' in startPendinggSubscription) {
          pendingPurchaseId =
            startPendinggSubscription.artist?.pendingPurchasePatron?.id ?? '#'
        }
        return router.replace(
          generatePath(routerPages.ArtistMembershipKonbiniCompletion, {
            artistId,
            productId: router.query.productId,
            pendingPurchaseId,
          })
        )
      },
    })

  const product = useMemo(() => {
    const activeProduct = getActiveMembershipSubscriptionProductPlan(
      data?.artist,
      initialPlan?.planId
    )
    if (activeProduct?.productId !== router.query.productId) {
      return
    }
    return activeProduct
  }, [data?.artist, initialPlan?.planId, router.query.productId])

  useEffect(() => {
    ;(async () => {
      const v = await getPrefrenceObject<ArtistMembershipPurchaseInput>(
        'artistMembershipPurchaseInput'
      )
      setInitialPlan({
        interval: v.interval,
        paymentMethod: v.paymentMethod,
        planId: v.planId,
      })
    })()
  }, [])

  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  return {
    state: {
      loading,
      artistId: artistId,
      product,
      paymentMethod: initialPlan?.paymentMethod,
      paymentSource: data?.user?.credit ?? null,
      isSubmitting:
        initialPlan?.paymentMethod === PaymentMethod.CreditCard
          ? startingSubscription
          : startingPendingSubscription,
      openExpiredCardModal,
      openPaymentErrorModal,
      openRequire3DSecureModal,
    },
    actions: {
      async onSubmit() {
        if (!initialPlan) {
          return
        }
        if (initialPlan.paymentMethod === PaymentMethod.CreditCard) {
          await startSubscription({
            variables: {
              artistId,
              planId: initialPlan.planId,
            },
          }).catch(error => {
            switch (error.graphQLErrors?.[0]?.extensions?.errorCode) {
              /**
               * @description
               * 3Dセキュア認証が必要な場合
               */
              case constants.errorCodes.REQUIRE_3D_SECURE:
                setOpenRequire3DSecureModal(true)
                return
              /**
               * @desciption
               * カードが有効期限切れの場合
               */
              case constants.errorCodes.EXPIRED_CARD:
                setOpenExpiredCardModal(true)
                return
              /**
               * @desciption
               * 何かしらの決済エラーの場合
               */
              case constants.errorCodes.PAYMENT_ERROR:
                setOpenPaymentErrorModal(true)
                return
              default:
                showError(error)
                break
            }
          })
          return
        }
        await startPendingSubscription({
          variables: {
            artistId,
            planId: initialPlan.planId,
          },
        }).catch(showError)
      },
      onCloseRequire3DSecureModal() {
        setOpenRequire3DSecureModal(false)
      },
      onCloseExpiredCardModal() {
        setOpenExpiredCardModal(false)
      },
      onClosePaymentErrorModal() {
        setOpenPaymentErrorModal(false)
      },
    },
  }
})

const QUERY = gql`
  query ArtistMembershipPurchaseConfirmation($id: ID) {
    artist(id: $id) {
      id
      ...ArtistMembershipSubscriptionDetail
    }
    user {
      id
      credit {
        ...PaymentDetail
      }
    }
  }
  ${ArtistMembershipSubscriptionDetailFragmentDoc}
  ${PaymentDetailFragmentDoc}
`

const START_PENDING_SUBSCRIPTION = gql`
  mutation StartPendinggSubscription($artistId: ID!, $planId: String!) {
    startPendinggSubscription(artistId: $artistId, planId: $planId) {
      ... on PendingPurchaseItem {
        id
      }
      ... on Patron {
        id
        artist {
          id
          pendingPurchasePatron {
            id
          }
        }
      }
    }
  }
`
